import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  CalendarClock,
  ClipboardList,
  FileText,
  Loader2,
  MessageCircle,
  ShieldCheck,
  Tag,
  User as UserIcon,
  type LucideIcon,
} from 'lucide-react';
import { apiClient } from '../api/client';
import { ORDER_STATUS_LABELS, type GlobalSearchResults } from '../types';
import { formatPhone } from '../utils/phone';
import { shortAddress } from '../utils/notificationView';
import SearchInput from './SearchInput';

/*
 * Общий поиск панели — поле «Найти…» в меню слева (владелец, 16.10.2026: «этот поиск сделаем
 * максимально умным, чтоб можно было найти всё, что угодно»). Раньше поле искало только
 * названия разделов меню. Теперь под ним выпадает выдача: разделы, наряды, акты, сотрудники,
 * справочник и прайс, контрагенты, объекты обслуживания, переписка. Как именно сервер
 * понимает запрос (адрес, телефон в любом виде, раскладка) — backend/src/search/search-query.ts.
 * Стрелки ↑↓ выбирают строку, Enter открывает, Esc закрывает; Ctrl+K — к полю из любого места.
 */

export interface SectionHit {
  path: string;
  label: string;
  icon: LucideIcon;
}

interface Hit {
  key: string;
  icon: LucideIcon;
  title: string;
  sub?: string;
  /** Пометка справа: статус наряда, «скрыт», «3 замечания» */
  tag?: string;
  tagTone?: 'danger' | 'muted';
  to: string;
}

interface Group {
  title: string;
  hits: Hit[];
}

const ROLE_LABELS: Record<string, string> = { ADMIN: 'Администратор', DISPATCHER: 'Диспетчер', TECHNICIAN: 'Мастер' };
const KB_SECTION_LABELS: Record<string, string> = { OBJECT: 'Объект', SERVICE: 'Услуга', RULE: 'Правило', OTHER: 'Справочник' };
const DATE = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

function toGroups(r: GlobalSearchResults, sections: SectionHit[]): Group[] {
  const groups: Group[] = [
    {
      title: 'Разделы',
      hits: sections.map((s) => ({ key: `section:${s.path}`, icon: s.icon, title: s.label, to: s.path })),
    },
    {
      title: 'Наряды',
      hits: r.orders.map((o) => ({
        key: `order:${o.id}`,
        icon: ClipboardList,
        title: `№${o.orderNumber} · ${shortAddress(o.address)}`,
        sub: [o.companyName || o.clientName, formatPhone(o.clientPhone), o.technicianName].filter(Boolean).join(' · '),
        tag: ORDER_STATUS_LABELS[o.status] ?? o.status,
        to: `/orders/${o.id}`,
      })),
    },
    {
      title: 'Акты',
      hits: r.acts.map((a) => ({
        key: `act:${a.id}`,
        icon: FileText,
        title: `Акт №${a.actNumber} · ${shortAddress(a.address)}`,
        sub: `${a.clientName} · наряд №${a.orderNumber} · ${DATE.format(new Date(a.createdAt))}`,
        to: `/orders/${a.orderId}`,
      })),
    },
    {
      title: 'Сотрудники',
      hits: r.people.map((p) => ({
        key: `person:${p.id}`,
        icon: UserIcon,
        title: p.fullName,
        sub: `${ROLE_LABELS[p.role] ?? p.role} · ${formatPhone(p.phone)}`,
        tag: p.isActive ? undefined : 'скрыт',
        tagTone: 'muted',
        to: `/employees?tech=${p.id}`,
      })),
    },
    {
      title: 'Справочник',
      hits: [
        ...r.articles.map((a) => ({
          key: `kb:${a.id}`,
          icon: BookOpen,
          title: a.title,
          sub: a.snippet.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim() || undefined,
          tag: KB_SECTION_LABELS[a.section],
          tagTone: 'muted' as const,
          to: `/kb?article=${a.id}`,
        })),
        ...r.prices.map((p) => ({
          key: `price:${p.id}`,
          icon: Tag,
          title: p.name,
          sub: p.defaultPrice ? `${Number(p.defaultPrice).toLocaleString('ru-RU')} ₽ за ${p.defaultUnit}` : 'Прайс',
          to: `/kb?q=${encodeURIComponent(p.name)}`,
        })),
      ],
    },
    {
      title: 'Контрагенты',
      hits: r.counterparties.map((c) => ({
        key: `inn:${c.inn}`,
        icon: ShieldCheck,
        title: c.name ?? `ИНН ${c.inn}`,
        sub: `ИНН ${c.inn}`,
        tag: c.problems > 0 ? `${c.problems} ${plural(c.problems, 'замечание', 'замечания', 'замечаний')}` : undefined,
        tagTone: 'danger',
        to: `/counterparties?q=${c.inn}`,
      })),
    },
    {
      title: 'Обслуживание',
      hits: r.objects.map((o) => ({
        key: `object:${o.id}`,
        icon: CalendarClock,
        title: o.name,
        sub: [o.companyTitle, o.address && shortAddress(o.address)].filter(Boolean).join(' · ') || undefined,
        to: `/maintenance?q=${encodeURIComponent(o.name)}`,
      })),
    },
    {
      title: 'Чат',
      hits: r.messages.map((m) => ({
        key: `msg:${m.id}`,
        icon: MessageCircle,
        title: m.threadTitle,
        sub: `${m.senderName}: ${m.text.replace(/\s+/g, ' ')}`,
        tag: DATE_TIME.format(new Date(m.createdAt)),
        tagTone: 'muted',
        to: `/chat?thread=${encodeURIComponent(m.thread)}`,
      })),
    },
  ];
  return groups.filter((g) => g.hits.length > 0);
}

/** Слова запроса для подсветки — так же, как их режет сервер: «ё» = «е», без окончаний */
function highlightWords(query: string): string[] {
  return query
    .toLowerCase()
    .replace(/ё/g, 'е')
    .split(/[^0-9a-zа-я]+/i)
    .filter((w) => w.length >= 2)
    .map((w) => (/^[а-я]+$/.test(w) && w.length >= 7 ? w.slice(0, -2) : /^[а-я]+$/.test(w) && w.length === 6 ? w.slice(0, -1) : w));
}

function Highlight({ text, words }: { text: string; words: string[] }) {
  if (!words.length) return <>{text}</>;
  // «ё» → «е» — замена буквы на букву, позиции в строке не сдвигаются
  const hay = text.toLowerCase().replace(/ё/g, 'е');
  const marks: Array<[number, number]> = [];
  for (const w of words) {
    let i = hay.indexOf(w);
    while (i >= 0) {
      marks.push([i, i + w.length]);
      i = hay.indexOf(w, i + w.length);
    }
  }
  if (!marks.length) return <>{text}</>;
  marks.sort((a, b) => a[0] - b[0]);
  const parts: ReactNode[] = [];
  let pos = 0;
  for (const [start, end] of marks) {
    if (end <= pos) continue;
    const from = Math.max(start, pos);
    if (from > pos) parts.push(text.slice(pos, from));
    parts.push(<mark key={from}>{text.slice(from, end)}</mark>);
    pos = end;
  }
  parts.push(text.slice(pos));
  return <>{parts}</>;
}

interface GlobalSearchProps {
  query: string;
  onQueryChange: (q: string) => void;
  /** Разделы меню, подходящие под запрос (с синонимами — см. NAV_SECTIONS в Layout.tsx) */
  sections: SectionHit[];
  /** После перехода: очистить поле, закрыть меню на телефоне */
  onNavigate: () => void;
  inputRef?: Ref<HTMLInputElement>;
}

export default function GlobalSearch({ query, onQueryChange, sections, onNavigate, inputRef }: GlobalSearchProps) {
  const navigate = useNavigate();
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState<GlobalSearchResults | null>(null);
  const [resultsFor, setResultsFor] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState<CSSProperties>({});
  const boxRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const requestSeq = useRef(0);

  const q = query.trim();
  const open = focused && q.length > 0;

  // Запрос к серверу — когда диспетчер перестал печатать; ответ на устаревший запрос
  // (печатал дальше) отбрасываем, чтобы выдача не прыгала
  useEffect(() => {
    if (q.length < MIN_QUERY) {
      setResults(null);
      setResultsFor('');
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      apiClient
        .get<GlobalSearchResults>('/search', { params: { q } })
        .then(({ data }) => {
          if (seq !== requestSeq.current) return;
          setResults(data);
          setResultsFor(q);
          setFailed(false);
        })
        .catch(() => {
          if (seq === requestSeq.current) setFailed(true);
        })
        .finally(() => {
          if (seq === requestSeq.current) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [q]);

  useEffect(() => setActive(-1), [q]);

  // Выдача шире меню — рисуется поверх страницы, справа от меню; на телефоне — во всю ширину
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (window.innerWidth <= 860) {
        setPosition({ top: rect.bottom + 6, left: 8, right: 8, maxHeight: `calc(100vh - ${Math.round(rect.bottom) + 6 + 76}px)` });
      } else {
        const sidebar = boxRef.current?.closest('.sidebar')?.getBoundingClientRect();
        const left = (sidebar?.right ?? rect.right) + 8;
        setPosition({ top: rect.top, left, width: Math.min(600, window.innerWidth - left - 16), maxHeight: `calc(100vh - ${Math.round(rect.top) + 16}px)` });
      }
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (boxRef.current?.contains(t) || popupRef.current?.contains(t)) return;
      setFocused(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const groups = toGroups(
    resultsFor === q && results ? results : { switchedTo: null, orders: [], acts: [], people: [], articles: [], prices: [], counterparties: [], objects: [], messages: [] },
    sections,
  );
  const flat = groups.flatMap((g) => g.hits);
  const words = highlightWords(resultsFor === q && results?.switchedTo ? results.switchedTo : q);

  useEffect(() => {
    if (active < 0) return;
    popupRef.current?.querySelector(`[data-hit="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function go(hit: Hit) {
    navigate(hit.to);
    setFocused(false);
    onNavigate();
    (document.activeElement as HTMLElement | null)?.blur();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown' && flat.length) {
      e.preventDefault();
      setActive((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp' && flat.length) {
      e.preventDefault();
      setActive((i) => (i <= 0 ? flat.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      const hit = flat[active >= 0 ? active : 0];
      if (hit) {
        e.preventDefault();
        go(hit);
      }
    } else if (e.key === 'Escape') {
      if (query) {
        // Первый Esc только очищает поиск, меню на телефоне закроет уже следующий
        e.stopPropagation();
        onQueryChange('');
      } else {
        setFocused(false);
        (e.target as HTMLElement).blur();
      }
    }
  }

  const waiting = q.length >= MIN_QUERY && (loading || resultsFor !== q);
  let index = -1;

  return (
    <div className="sidebar-search" ref={boxRef} onKeyDown={onKeyDown}>
      <SearchInput
        value={query}
        onChange={(v) => {
          onQueryChange(v);
          setFocused(true);
        }}
        onFocus={() => setFocused(true)}
        inputRef={inputRef}
        placeholder="Найти всё… (Ctrl+K)"
        compact
      />
      {open &&
        createPortal(
          <div className="global-search" ref={popupRef} style={position} role="listbox" aria-label="Результаты поиска">
            {results?.switchedTo && resultsFor === q && (
              <div className="global-search-note">
                Показано для «{results.switchedTo}» — похоже, была включена английская раскладка
              </div>
            )}
            {groups.map((g) => (
              <div key={g.title} className="global-search-group">
                <div className="global-search-group-title">{g.title}</div>
                {g.hits.map((hit) => {
                  index++;
                  const i = index;
                  return (
                    <button
                      key={hit.key}
                      type="button"
                      data-hit={i}
                      role="option"
                      aria-selected={i === active}
                      className={`global-search-hit ${i === active ? 'active' : ''}`}
                      onMouseEnter={() => setActive(i)}
                      // mousedown, а не click: иначе поле теряет фокус и выдача закрывается раньше
                      onMouseDown={(e) => {
                        e.preventDefault();
                        go(hit);
                      }}
                    >
                      <hit.icon size={16} className="global-search-hit-icon" />
                      <span className="global-search-hit-text">
                        <span className="global-search-hit-title">
                          <Highlight text={hit.title} words={words} />
                        </span>
                        {hit.sub && (
                          <span className="global-search-hit-sub">
                            <Highlight text={hit.sub} words={words} />
                          </span>
                        )}
                      </span>
                      {hit.tag && <span className={`global-search-tag ${hit.tagTone ?? ''}`}>{hit.tag}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
            {waiting && (
              <div className="global-search-status">
                <Loader2 size={14} className="spin" /> Ищем по нарядам, актам, сотрудникам, справочнику и чату…
              </div>
            )}
            {!waiting && failed && <div className="global-search-status error-text">Не удалось выполнить поиск — проверьте связь</div>}
            {!waiting && !failed && q.length < MIN_QUERY && flat.length === 0 && (
              <div className="global-search-status">Наберите ещё хотя бы одну букву</div>
            )}
            {!waiting && !failed && q.length >= MIN_QUERY && flat.length === 0 && (
              <div className="global-search-status">
                Ничего не нашлось. Ищется по адресу, клиенту, телефону, номеру наряда или акта, ИНН, мастеру, справочнику и чату
              </div>
            )}
            {flat.length > 0 && <div className="global-search-hint">↑↓ — выбрать · Enter — открыть · Esc — закрыть</div>}
          </div>,
          document.body,
        )}
    </div>
  );
}
